import {
  Autocomplete,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  maxCharactersValidator,
  requiredValidator,
  uniqueValidator,
} from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../../../api/adminApi";
import type { FundWithFieldValues } from "../../../../../../api/types/fundStructureTypes";
import type { Fundraising } from "../../../../../../api/types/fundraisingTypes";
import type { SelectOption } from "../../../../../common/filters/filterTypes";
import useFundraisingDetailsPageContext from "../useFundraisingDetailsPageContext";

interface Props {
  allFunds: FundWithFieldValues[];
  onSubmit: (updatedFundraising: Fundraising) => void;
  onAsyncOperationStart: (asyncOperationId: string) => void;
  onClose: () => void;
}

interface FormState {
  newFundName: string;
  validationResult: ValidationResult;
  copyFromFundEnabled: boolean;
  templateFund: SelectOption | null;
}

const updateFundraisingAccess = withErrorHandling(adminApi.updateFundraisingAccess);

const maxNameLength = 100;

const CreateFundDialog = ({ allFunds, onSubmit, onAsyncOperationStart, onClose }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const { fundraising, onConfirmSave } = useFundraisingDetailsPageContext();

  const existingFundNames = useMemo(() => allFunds.map((fund) => fund.name), [allFunds]);

  const copyFromFundOptions = useMemo(
    () => allFunds.map((fund) => ({ value: fund.id, label: fund.name })).sort(stringComparerBy((o) => o.label)),
    [allFunds]
  );

  const [isSaving, setIsSaving] = useState(false);

  const [formState, setFormState] = useState<FormState>({
    newFundName: "",
    validationResult: { isValid: false, error: "" },
    copyFromFundEnabled: false,
    templateFund: null,
  });

  const save = async () => {
    setIsSaving(true);
    const [resp, error] = await updateFundraisingAccess(fundraising.id, {
      expectedEtag: fundraising.etag,
      newFundraisingFundName: formState.newFundName.trim(),
      copyInvestorsFromFundId: formState.copyFromFundEnabled ? formState.templateFund?.value : undefined,
    });
    setIsSaving(false);

    if (error) {
      logError(error, "[CreateFundDialog]");
      sendNotificationError(error.message);
    } else {
      sendNotification("Fundraising updated successfully with new fund");
      onClose();

      if (resp.fundraising) {
        onSubmit(resp.fundraising);
      } else {
        onAsyncOperationStart(resp.asyncOperationId);
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const validateFundName = combineValidators(
      requiredValidator,
      maxCharactersValidator(maxNameLength),
      uniqueValidator("A fund with this name already exists", existingFundNames)
    );

    setFormState((prev) => ({
      ...prev,
      newFundName: e.target.value,
      validationResult: validateFundName(e.target.value.toLowerCase()),
    }));
  };

  const handleCopyFromFundChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFormState((prev) => ({
      ...prev,
      copyFromFundEnabled: checked,
      templateFund: checked ? prev.templateFund : null,
    }));
  };

  const handleSave = () => onConfirmSave(save);

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Create Fund</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2.5} sx={{ alignItems: "flex-start" }}>
          <TextField
            fullWidth
            label="Name"
            value={formState.newFundName}
            onChange={handleNameChange}
            helperText={formState.validationResult.error}
            error={Boolean(formState.validationResult.error)}
            slotProps={{
              htmlInput: { maxLength: maxNameLength },
              input: {
                endAdornment: (
                  <Typography variant="caption" color="textSecondary">
                    {maxNameLength - formState.newFundName.length}
                  </Typography>
                ),
              },
            }}
          />

          <FormControlLabel
            control={<Checkbox checked={formState.copyFromFundEnabled} onChange={handleCopyFromFundChange} />}
            label="Copy contacts from another fund"
          />

          <Autocomplete
            disabled={!formState.copyFromFundEnabled}
            openOnFocus
            fullWidth
            options={copyFromFundOptions}
            isOptionEqualToValue={(opt, val) => opt.value === val.value}
            renderInput={(params) => <TextField {...params} placeholder="Source Fund" />}
            value={formState.templateFund}
            noOptionsText="No funds found"
            onChange={(_, newValue) => {
              if (newValue) {
                setFormState((prev) => ({
                  ...prev,
                  templateFund: newValue,
                }));
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          variant="contained"
          loading={isSaving}
          onClick={handleSave}
          disabled={!formState.validationResult.isValid}
        >
          Create & Save
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default CreateFundDialog;
